import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Calendar, ArrowRight, X } from 'lucide-react';
import { resolveImageUrl } from '@/utils/imageUrl';

export const ReferencesSection = ({ references }) => {
  const [selected, setSelected] = useState(null);

  if (!references || references.length === 0) return null;

  return (
    <section id="referenzen" className="py-20 md:py-32 bg-white" data-testid="references-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <span className="section-label text-[#CC2229]">Projekte</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 mt-4 tracking-tight uppercase">
            Unsere Referenzen
          </h2>
          <p className="text-slate-500 mt-4 max-w-2xl text-base sm:text-lg">
            Ausgewählte Projekte aus Dresden und Umgebung — Qualitätsarbeit, die überzeugt.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {references.map((ref, i) => (
            <motion.div
              key={ref.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group cursor-pointer"
              onClick={() => setSelected(ref)}
              data-testid={`reference-card-${i}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                <img
                  src={resolveImageUrl(ref.image_url)}
                  alt={ref.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0077D4]/80 via-transparent to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />

                {/* Category badge */}
                <div className="absolute top-4 left-4">
                  <span className="bg-[#CC2229] text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5">
                    {ref.category}
                  </span>
                </div>

                {/* Bottom info */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-heading text-lg font-bold text-white tracking-tight">
                    {ref.title}
                  </h3>
                  <div className="flex items-center gap-4 mt-2 text-xs text-white/70">
                    {ref.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={12} /> {ref.location}
                      </span>
                    )}
                    {ref.year && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={12} /> {ref.year}
                      </span>
                    )}
                  </div>
                </div>

                {/* Hover arrow */}
                <div className="absolute bottom-5 right-5 w-10 h-10 bg-white/0 group-hover:bg-white flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100">
                  <ArrowRight size={18} className="text-[#0077D4]" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelected(null)}
            data-testid="reference-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="relative aspect-video">
                <img src={resolveImageUrl(selected.image_url)} alt={selected.title} className="w-full h-full object-cover" />
                <button
                  onClick={() => setSelected(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
                  data-testid="close-reference-modal"
                >
                  <X size={20} className="text-slate-900" />
                </button>
                <div className="absolute top-4 left-4">
                  <span className="bg-[#CC2229] text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5">
                    {selected.category}
                  </span>
                </div>
              </div>
              <div className="p-8">
                <h3 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">{selected.title}</h3>
                <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                  {selected.location && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {selected.location}</span>}
                  {selected.year && <span className="inline-flex items-center gap-1"><Calendar size={14} /> {selected.year}</span>}
                </div>
                <p className="text-slate-600 mt-6 leading-relaxed">{selected.description}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
